import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/api';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import { toast } from 'sonner';

const CheckoutItemDetails = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchItem = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/checkouts/${id}`);
        setItem(data);
      } catch (e) {
        console.error('[CheckoutItemDetails] Failed to load checkout item:', e);
        setError(e.response?.data?.error || 'Failed to load checkout item');
        toast.error('Failed to load checkout item');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!item) return <div className="p-6">No checkout item found.</div>;

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Checkout Item Details</h1>
          <div className="flex gap-2">
            <Link to="/checkout"><Button variant="outline">Back to Checkout</Button></Link>
            <Link to="/reports"><Button variant="outline">Reports</Button></Link>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-48 h-48 bg-muted rounded flex items-center justify-center overflow-hidden">
              {item.imageUrl ? (
                <AppImage src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <div className="text-muted-foreground">No Image</div>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-sm text-muted-foreground">Name</div>
                <div className="font-medium">{item.name}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Barcode</div>
                <div className="font-mono">{item.barcode || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">SKU</div>
                <div className="font-mono">{item.sku || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Quantity</div>
                <div>{item.quantity}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Price</div>
                <div>${Number(item.price || 0).toFixed(2)}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Category</div>
                <div>{Array.isArray(item.category) ? item.category.join(', ') : (item.category || 'N/A')}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Supplier</div>
                <div>{item.supplier || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Description</div>
                <div>{item.description || '—'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Created At</div>
                <div>{item.createdAt ? new Date(item.createdAt.seconds ? item.createdAt.seconds * 1000 : item.createdAt).toLocaleString() : 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Updated At</div>
                <div>{item.updatedAt ? new Date(item.updatedAt.seconds ? item.updatedAt.seconds * 1000 : item.updatedAt).toLocaleString() : 'N/A'}</div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-2">Raw Data</h2>
            <pre className="bg-muted p-3 rounded text-xs overflow-auto">{JSON.stringify(item, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutItemDetails;